import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Login from "@/pages/Login";
import AppShell from "@/layouts/AppShell";
import Dashboard from "@/pages/Dashboard";
import Inbox from "@/pages/Inbox";
import Companies from "@/pages/Companies";
import CompanyDetail from "@/pages/CompanyDetail";
import Departments from "@/pages/Departments";
import EmailAccounts from "@/pages/EmailAccounts";
import Users from "@/pages/Users";
import Templates from "@/pages/Templates";
import KnowledgeBase from "@/pages/KnowledgeBase";
import Contacts from "@/pages/Contacts";
import Tickets from "@/pages/Tickets";
import Approvals from "@/pages/Approvals";
import AuditLogs from "@/pages/AuditLogs";
import Agents from "@/pages/Agents";
import Settings from "@/pages/Settings";
import Rules from "@/pages/Rules";
import BulkEmail from "@/pages/BulkEmail";
import BulkShell from "@/pages/bulk/BulkShell";
import BulkDashboard from "@/pages/bulk/BulkDashboard";
import Campaigns from "@/pages/bulk/Campaigns";
import CampaignDetail from "@/pages/bulk/CampaignDetail";
import BulkTemplates from "@/pages/bulk/BulkTemplates";
import SmtpProfiles from "@/pages/bulk/SmtpProfiles";
import DeliveryLogs from "@/pages/bulk/DeliveryLogs";
import Suppressions from "@/pages/bulk/Suppressions";
import BulkAnalytics from "@/pages/bulk/BulkAnalytics";
import Billing from "@/pages/Billing";
import Signatures from "@/pages/Signatures";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-muted-foreground text-sm" data-testid="auth-loading">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="inbox" element={<Inbox />} />
              <Route path="inbox/:folder" element={<Inbox />} />
              <Route path="companies" element={<Companies />} />
              <Route path="companies/:id" element={<CompanyDetail />} />
              <Route path="departments" element={<Departments />} />
              <Route path="email-accounts" element={<EmailAccounts />} />
              <Route path="users" element={<Users />} />
              <Route path="templates" element={<Templates />} />
              <Route path="knowledge" element={<KnowledgeBase />} />
              <Route path="contacts" element={<Contacts />} />
              <Route path="tickets" element={<Tickets />} />
              <Route path="approvals" element={<Approvals />} />
              <Route path="audit-logs" element={<AuditLogs />} />
              <Route path="agents" element={<Agents />} />
              <Route path="rules" element={<Rules />} />
              <Route path="bulk" element={<BulkShell />}>
                <Route index element={<BulkDashboard />} />
                <Route path="analytics" element={<BulkAnalytics />} />
                <Route path="campaigns" element={<Campaigns />} />
                <Route path="campaigns/:id" element={<CampaignDetail />} />
                <Route path="templates" element={<BulkTemplates />} />
                <Route path="smtp-profiles" element={<SmtpProfiles />} />
                <Route path="suppressions" element={<Suppressions />} />
                <Route path="logs" element={<DeliveryLogs />} />
              </Route>
              <Route path="bulk-legacy" element={<BulkEmail />} />
              <Route path="billing" element={<Billing />} />
              <Route path="signatures" element={<Signatures />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}
